/**
 * Builds a ReportSnapshot — UX/UI Specification §16.
 *
 * This is the one place the rules of a report live. The wizard calls it to
 * render the preview and the Worker calls it to produce the snapshot it
 * freezes and renders to PDF. Because it is the same pure function over the
 * same source data, a preview cannot disagree with the delivered document —
 * which is the whole reason the report is built this way (Architecture §12.1).
 *
 * Everything derived is derived here: deltas, BMI, chart axes, goal
 * percentages, labels, the student's age. The template does arithmetic only
 * for layout (§16.1).
 */

import {
  COVER_MESSAGE_MAX_LENGTH,
  CUSTOM_COVER_MESSAGE_KEY,
  findCoverMessage,
} from '../constants/cover-messages.js';
import {
  calculateGoalProgress,
  GOAL_TYPE_METRIC,
  resolveGoalCurrentValue,
  type GoalType,
} from '../constants/goals.js';
import {
  ASSESSMENT_KEYS,
  ASSESSMENT_LABELS,
  FULL_BODY_CATEGORIES,
  METRICS,
  METRIC_TABLE_ORDER,
  muscleLabel,
  muscleUnitSymbol,
  PHOTO_CATEGORY_LABELS,
  POSTURE_ANGLES,
  type AssessmentKey,
  type MetricKey,
  type PhotoCategory,
  type PostureAngle,
} from '../constants/metrics.js';
import { calculateAge } from '../format/tr.js';
import { buildCharts, buildCustomChart } from '../measurements/charts.js';
import {
  customMetricSeries,
  customMetricsOf,
  latestValue,
  metricSeries,
  type MeasurementRecord,
} from '../measurements/resolve.js';
import type {
  ReportSnapshot,
  SnapshotAssessment,
  SnapshotAssessmentScore,
  SnapshotCustomMetric,
  SnapshotGoal,
  SnapshotMetric,
  SnapshotMetricPoint,
  SnapshotPhoto,
  SnapshotPhotoPair,
  SnapshotPostureAssessment,
  SnapshotPosturePhoto,
  SnapshotStudio,
} from '../types/report-snapshot.js';

/* ---------------------------------------------------------------- source */

export interface PhotoRecord {
  id: string;
  capturedAt: string;
  category: PhotoCategory;
  customLabel: string | null;
  r2Key: string;
  mimeType: string;
  widthPx: number | null;
  heightPx: number | null;
}

export interface GoalRecord {
  id: string;
  goalType: GoalType;
  /** Set only when goalType is 'custom_metric'; identifies which of the
   *  student's own custom measurement fields this goal tracks. */
  customMetricLabel: string | null;
  title: string;
  goalMode: 'numeric' | 'qualitative';
  startValue: number | null;
  currentValue: number | null;
  targetValue: number | null;
  unit: string | null;
  targetDate: string | null;
  qualitativeNote: string | null;
  status: 'active' | 'completed' | 'cancelled';
}

export interface AssessmentRecord {
  id: string;
  assessedAt: string;
  coreControl: number | null;
  balance: number | null;
  flexibility: number | null;
  lowerBodyStrength: number | null;
  upperBodyControl: number | null;
  generalNote: string | null;
  nextPeriodFocus: string | null;
}

export interface PosturePhotoRecord {
  id: string;
  angle: PostureAngle;
  r2Key: string;
  mimeType: string;
  widthPx: number | null;
  heightPx: number | null;
  linePositionPercent: number | null;
}

export interface PostureAssessmentRecord {
  id: string;
  assessedAt: string;
  score: number | null;
  note: string | null;
  photos: PosturePhotoRecord[];
}

export interface ReportSource {
  studio: SnapshotStudio;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    gender: 'female' | 'male' | 'other';
    dateOfBirth: string;
    heightCm: number;
    profilePhotoR2Key: string | null;
  };
  instructor: { id: string; name: string } | null;
  period: { start: string; end: string };
  measurements: MeasurementRecord[];
  photos: PhotoRecord[];
  goals: GoalRecord[];
  assessments: AssessmentRecord[];
  postureAssessments: PostureAssessmentRecord[];
  /** Set once at generation time; the preview passes the current instant. */
  generatedAt: string;
}

/* ------------------------------------------------------------ selections */

export interface MetricOverride {
  startMeasurementId?: string | null;
  currentMeasurementId?: string | null;
}

export interface PhotoPairSelection {
  category: PhotoCategory;
  /** null renders a single photo rather than a comparison. */
  leftPhotoId: string | null;
  rightPhotoId: string;
}

export interface ReportSelections {
  /** Metrics the instructor kept. Order is ignored; the table order is fixed. */
  includedMetrics: MetricKey[];
  /** Per-metric overrides of the automatically chosen records (D-03). */
  metricOverrides: Partial<Record<MetricKey, MetricOverride>>;
  photoPairs: PhotoPairSelection[];
  regionalNote: string | null;
  /** Ordered; the order is frozen into the snapshot. */
  goalIds: string[];
  assessmentId: string | null;
  /**
   * 0-3 ids (POSTURE_COMPARISON_MAX), in whatever order the wizard checked
   * them — buildPosture sorts the snapshot by date, so selection order
   * never matters.
   */
  postureAssessmentIds: string[];
  /** Folded labels of the studio's own metrics the instructor kept. */
  includedCustomLabels: string[];
  packageReminder: { enabled: boolean; remainingLessons: number | null };
  coverMessageKey: string | null;
  /**
   * The instructor's own cover sentence (Constitution v1.3 §8, exception 3).
   * Takes precedence over `coverMessageKey`, so the two can never both apply.
   */
  coverMessageCustom: string | null;
}

/* --------------------------------------------------------------- helpers */

/**
 * The period bounds every dated record the report draws on: measurements,
 * photographs and assessments alike. Nothing recorded outside it reaches the
 * page, whether the wizard offered it or not — this is the one place that
 * decides, so a selection made before the dates were changed cannot smuggle
 * anything in.
 */
function inPeriod(source: ReportSource, date: string): boolean {
  return date >= source.period.start && date <= source.period.end;
}

function withinPeriod<T extends { measuredAt: string }>(
  source: ReportSource,
  records: T[],
): T[] {
  return records.filter((record) => inPeriod(source, record.measuredAt));
}

/**
 * Resolves the pair of records a metric is reported against — §11.1.
 *
 *   current = newest value inside the period
 *   start   = earliest value inside the period, before that one
 *
 * D-01 was reversed on 6 September 2026. It previously took Başlangıç from the
 * student's all-time first record, on the reasoning that the report answers
 * how far the student has come. In use that read as a leak: a report headed
 * "1 – 30 September" showed a change measured from March. A period the
 * instructor chose has to mean what it says, or it means nothing.
 *
 * The whole history is still one click away — the period step offers
 * "Tüm zamanlar", which sets the period to the student's first and last
 * records and produces exactly the report D-01 used to force on every period.
 */
function resolveMetric(
  source: ReportSource,
  key: MetricKey,
  override: MetricOverride | undefined,
): { start: SnapshotMetricPoint | null; current: SnapshotMetricPoint } | null {
  const series = metricSeries(
    withinPeriod(source, source.measurements),
    key,
    source.student.heightCm,
  );
  if (series.length === 0) return null;

  const currentId = override?.currentMeasurementId;
  const current = currentId
    ? (series.find((p) => p.measurementId === currentId) ??
      (series[series.length - 1] as SnapshotMetricPoint))
    : (series[series.length - 1] as SnapshotMetricPoint);

  const before = series.filter(
    (p) => p.measuredAt < current.measuredAt || p.measurementId !== current.measurementId,
  );
  const earlier = before.filter((p) => p.measuredAt <= current.measuredAt);

  const startId = override?.startMeasurementId;
  let start: SnapshotMetricPoint | null;

  if (startId === null) {
    start = null;
  } else if (startId) {
    start = series.find((p) => p.measurementId === startId) ?? null;
  } else {
    const first = earlier[0];
    start = first && first.measurementId !== current.measurementId ? first : null;
  }

  if (start && start.measurementId === current.measurementId) start = null;

  return { start, current };
}

function buildMetric(
  source: ReportSource,
  key: MetricKey,
  override: MetricOverride | undefined,
): SnapshotMetric | null {
  const resolved = resolveMetric(source, key, override);
  if (!resolved) return null;

  const { start, current } = resolved;
  const definition = METRICS[key];

  const currentRecord = source.measurements.find((m) => m.id === current.measurementId);
  const startRecord = start
    ? source.measurements.find((m) => m.id === start.measurementId)
    : null;

  const isMuscle = key === 'muscle';
  const currentUnit = isMuscle ? (currentRecord?.muscleUnit ?? null) : null;
  const startUnit = isMuscle ? (startRecord?.muscleUnit ?? null) : null;

  // Subtracting a percentage from a weight would produce a plausible-looking
  // number with no meaning, in a document that goes to a client (§11.3).
  const unitsDiffer = isMuscle && start !== null && startUnit !== currentUnit;

  return {
    key,
    label: isMuscle && currentUnit ? muscleLabel(currentUnit) : definition.label,
    unit: isMuscle && currentUnit ? muscleUnitSymbol(currentUnit) : definition.unit,
    decimals: definition.decimals,
    start: unitsDiffer ? null : start,
    current,
    changeAbs: start && !unitsDiffer ? current.value - start.value : null,
    comparisonBlockedReason: unitsDiffer ? 'MUSCLE_UNIT_MISMATCH' : null,
  };
}

function toSnapshotPhoto(photo: PhotoRecord): SnapshotPhoto {
  return {
    photoId: photo.id,
    capturedAt: photo.capturedAt,
    r2Key: photo.r2Key,
    mimeType: photo.mimeType,
    widthPx: photo.widthPx,
    heightPx: photo.heightPx,
  };
}

function photoLabel(photo: PhotoRecord): string {
  return photo.category === 'custom' && photo.customLabel
    ? photo.customLabel
    : PHOTO_CATEGORY_LABELS[photo.category];
}

/**
 * Builds one comparison. The left label is derived, never typed: it reads
 * "Başlangıç" when the left photo is the student's earliest in that category
 * and "Önceki" otherwise (§15.1).
 *
 * With no left photo there is nothing to compare against, and the right one
 * stops being "Güncel" — a word that only means anything next to a before.
 * It is a single photograph from the period and says so.
 */
function buildPair(
  source: ReportSource,
  selection: PhotoPairSelection,
): SnapshotPhotoPair | null {
  const photos = source.photos.filter((p) => inPeriod(source, p.capturedAt));

  const right = photos.find((p) => p.id === selection.rightPhotoId);
  if (!right) return null;

  const left = selection.leftPhotoId
    ? (photos.find((p) => p.id === selection.leftPhotoId) ?? null)
    : null;

  const sameCategory = photos
    .filter((p) => p.category === right.category && p.customLabel === right.customLabel)
    .sort((a, b) => (a.capturedAt < b.capturedAt ? -1 : 1));

  const earliest = sameCategory[0];
  const leftIsEarliest = left !== null && earliest?.id === left.id;

  return {
    category: right.category,
    label: photoLabel(right),
    leftLabel: leftIsEarliest ? 'Başlangıç' : 'Önceki',
    rightLabel: left ? 'Güncel' : 'Bu Dönem',
    left: left ? toSnapshotPhoto(left) : null,
    right: toSnapshotPhoto(right),
  };
}

/**
 * The latest in-period measurement point behind a goal, however it is
 * tracked: a fixed metric through `GOAL_TYPE_METRIC`, or — for
 * 'custom_metric' — the student's own field, matched by its folded label
 * the same way the measurement charts match one.
 */
function goalMeasuredPoint(
  goal: GoalRecord,
  periodMeasurements: readonly MeasurementRecord[],
  heightCm: number | null,
) {
  if (goal.goalType === 'custom_metric') {
    if (!goal.customMetricLabel) return null;
    const series = customMetricSeries(periodMeasurements, goal.customMetricLabel);
    return series.length > 0 ? series[series.length - 1] : null;
  }

  const metric = GOAL_TYPE_METRIC[goal.goalType];
  return metric ? latestValue(periodMeasurements, metric, heightCm) : null;
}

/**
 * `measured` is the latest in-period value for this goal's metric.
 *
 * A goal on weight or waist reads its current value from the measurements
 * rather than from a number typed into the goal form, so the report and the
 * physical table cannot disagree about what the student weighs. Frozen here
 * like everything else in a snapshot: a later measurement produces a new
 * version, never a changed one.
 */
function buildGoal(
  goal: GoalRecord,
  order: number,
  measured: number | null,
): SnapshotGoal {
  const currentValue = resolveGoalCurrentValue(
    goal.goalType,
    goal.currentValue,
    measured,
  );

  return {
    goalId: goal.id,
    order,
    goalType: goal.goalType,
    title: goal.title,
    mode: goal.goalMode,
    unit: goal.unit,
    startValue: goal.startValue,
    currentValue,
    targetValue: goal.targetValue,
    targetDate: goal.targetDate,
    progressPct:
      goal.goalMode === 'numeric'
        ? calculateGoalProgress(goal.startValue, currentValue, goal.targetValue)
        : null,
    qualitativeNote: goal.qualitativeNote,
    status: goal.status === 'completed' ? 'completed' : 'active',
  };
}

const ASSESSMENT_FIELD: Record<AssessmentKey, keyof AssessmentRecord> = {
  coreControl: 'coreControl',
  balance: 'balance',
  flexibility: 'flexibility',
  lowerBodyStrength: 'lowerBodyStrength',
  upperBodyControl: 'upperBodyControl',
};

function buildAssessment(
  source: ReportSource,
  assessmentId: string | null,
): SnapshotAssessment | null {
  if (!assessmentId) return null;

  const chosen = source.assessments.find(
    (a) => a.id === assessmentId && inPeriod(source, a.assessedAt),
  );
  if (!chosen) return null;

  /*
   * The previous assessment is the most recent one before the chosen date,
   * found rather than selected — the instructor picks one assessment, not two.
   *
   * It is deliberately *not* bounded by the period. It contributes no content
   * of its own; it is the column the chosen scores are compared against, and a
   * comparison that silently vanishes because the earlier session fell a week
   * outside the dates would be worse than useless.
   */
  const previous =
    [...source.assessments]
      .filter((a) => a.assessedAt < chosen.assessedAt)
      .sort((a, b) => (a.assessedAt < b.assessedAt ? 1 : -1))[0] ?? null;

  const scores: SnapshotAssessmentScore[] = [];
  for (const key of ASSESSMENT_KEYS) {
    const field = ASSESSMENT_FIELD[key];
    const current = chosen[field];
    // A category with no current score does not render at all (§10.10).
    if (typeof current !== 'number') continue;

    const previousValue = previous ? previous[field] : null;
    scores.push({
      key,
      label: ASSESSMENT_LABELS[key],
      previous: typeof previousValue === 'number' ? previousValue : null,
      current,
    });
  }

  return {
    assessmentId: chosen.id,
    assessedAt: chosen.assessedAt,
    previousAssessmentId: previous?.id ?? null,
    previousAssessedAt: previous?.assessedAt ?? null,
    scores,
    generalNote: chosen.generalNote,
    nextPeriodFocus: chosen.nextPeriodFocus,
  };
}

const POSTURE_ANGLE_LABEL: Record<PostureAngle, string> = {
  front: 'Ön',
  side: 'Yan',
  back: 'Arka',
};

/**
 * Builds one session's photos/score/note into its snapshot shape, or null
 * when the session carries nothing (§16.1 rule 6: empty means absent).
 */
function buildOnePosture(
  chosen: PostureAssessmentRecord,
): SnapshotPostureAssessment | null {
  const byAngle = new Map(chosen.photos.map((p) => [p.angle, p]));
  const photos: SnapshotPosturePhoto[] = [];
  for (const angle of POSTURE_ANGLES) {
    const photo = byAngle.get(angle);
    if (!photo) continue;
    photos.push({
      angle,
      label: POSTURE_ANGLE_LABEL[angle],
      photo: {
        photoId: photo.id,
        capturedAt: chosen.assessedAt,
        r2Key: photo.r2Key,
        mimeType: photo.mimeType,
        widthPx: photo.widthPx,
        heightPx: photo.heightPx,
      },
      linePositionPercent: photo.linePositionPercent,
    });
  }

  if (photos.length === 0 && chosen.score === null && !chosen.note) return null;

  return {
    assessedAt: chosen.assessedAt,
    score: chosen.score,
    note: chosen.note,
    photos,
  };
}

/**
 * The period does not bound posture sessions the way it bounds everything
 * else (§ above) — the instructor picks explicit sessions in the wizard, the
 * same way they pick one assessment, so there is nothing for a date range to
 * filter.
 *
 * Sorted oldest first so every renderer downstream (web preview and PDF
 * alike) reads a consistent "before → after" order without re-sorting
 * itself. This function does not re-cap the id list at
 * `POSTURE_COMPARISON_MAX` — the wizard enforces the cap and the API schema
 * re-validates it; this is a pure function of whatever ids it is given.
 *
 * Two sessions can legitimately share `assessedAt` (no DB uniqueness
 * constraint — e.g. a retake after a bad photo), so the sort below is done on
 * the resolved *records* (which carry a stable `id`) rather than on the
 * mapped snapshot shape, and ties break on `id` — a comparator that never
 * returns 0 for equal dates would otherwise hand tied sessions an arbitrary,
 * non-deterministic order on every rebuild (final review, Important 2).
 *
 * The incoming id list is also de-duplicated before resolving: the API's
 * `.max(3)` Zod cap counts array entries, not distinct ids, so `["a","a","a"]`
 * would otherwise pass validation and render the same session three times.
 */
function buildPosture(
  source: ReportSource,
  postureAssessmentIds: string[],
): SnapshotPostureAssessment[] {
  const resolved = [...new Set(postureAssessmentIds)]
    .map((id) => source.postureAssessments.find((p) => p.id === id))
    .filter((p): p is PostureAssessmentRecord => p !== undefined)
    .sort((a, b) => {
      if (a.assessedAt < b.assessedAt) return -1;
      if (a.assessedAt > b.assessedAt) return 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });

  return resolved
    .map(buildOnePosture)
    .filter((p): p is SnapshotPostureAssessment => p !== null);
}

/* ----------------------------------------------------------------- build */

export function buildSnapshot(
  source: ReportSource,
  selections: ReportSelections,
): ReportSnapshot {
  const included = new Set(selections.includedMetrics);

  const metrics: SnapshotMetric[] = [];
  for (const key of METRIC_TABLE_ORDER) {
    if (!included.has(key)) continue;
    const metric = buildMetric(source, key, selections.metricOverrides[key]);
    if (metric) metrics.push(metric);
  }

  /*
   * The studio's own metrics, under the same rules as the built-in ones: the
   * period bounds them, and a metric with one value in it shows no change.
   */
  const includedCustom = new Set(selections.includedCustomLabels);
  const customMetrics: SnapshotCustomMetric[] = [];

  for (const metric of customMetricsOf(withinPeriod(source, source.measurements))) {
    if (!includedCustom.has(metric.labelNormalized)) continue;

    const series = customMetricSeries(
      withinPeriod(source, source.measurements),
      metric.labelNormalized,
    );
    const current = series[series.length - 1];
    if (!current) continue;

    const first = series[0];
    const start = first && first.measurementId !== current.measurementId ? first : null;

    customMetrics.push({
      labelNormalized: metric.labelNormalized,
      label: metric.label,
      unit: metric.unit,
      decimals: 1,
      start,
      current,
      changeAbs: start ? Number((current.value - start.value).toFixed(4)) : null,
    });
  }

  const charts = buildCharts(
    withinPeriod(source, source.measurements),
    source.student.heightCm,
    [...included],
    // Fallback only, and labelled where it is used: a physical page that is a
    // table and no picture is a poorer report than one that reaches back.
    source.measurements,
  );

  // A studio's own metric gets a chart like any other row of the table.
  for (const metric of customMetrics) {
    const chart = buildCustomChart(withinPeriod(source, source.measurements), metric);
    if (chart) charts.push(chart);
  }

  const pairs = selections.photoPairs
    .map((selection) => buildPair(source, selection))
    .filter((pair): pair is SnapshotPhotoPair => pair !== null);

  const fullBodyPhotoPairs = FULL_BODY_CATEGORIES.flatMap((category) =>
    pairs.filter((pair) => pair.category === category),
  );
  const regionalPhotoPairs = pairs.filter(
    (pair) => !FULL_BODY_CATEGORIES.includes(pair.category),
  );

  const periodMeasurements = withinPeriod(source, source.measurements);

  const goals = selections.goalIds
    .map((id, index) => {
      const goal = source.goals.find((g) => g.id === id);
      if (!goal) return null;

      const point = goalMeasuredPoint(goal, periodMeasurements, source.student.heightCm);
      return buildGoal(goal, index, point?.value ?? null);
    })
    .filter((goal): goal is SnapshotGoal => goal !== null);

  const coverMessage = resolveCoverMessage(selections);

  const profilePhoto = source.student.profilePhotoR2Key
    ? {
        photoId: source.student.id,
        capturedAt: source.period.end,
        r2Key: source.student.profilePhotoR2Key,
        mimeType: 'image/webp',
        widthPx: null,
        heightPx: null,
      }
    : null;

  return {
    schemaVersion: 1,
    generatedAt: source.generatedAt,
    locale: 'tr-TR',
    timezone: 'Europe/Istanbul',

    studio: source.studio,

    student: {
      studentId: source.student.id,
      fullName: `${source.student.firstName} ${source.student.lastName}`,
      // Frozen: a later height correction must not change an old report's BMI.
      age: calculateAge(source.student.dateOfBirth, source.period.end),
      gender: source.student.gender,
      heightCm: source.student.heightCm,
      profilePhoto,
    },

    instructor: source.instructor
      ? { instructorId: source.instructor.id, name: source.instructor.name }
      : null,

    period: source.period,

    metrics,
    customMetrics,
    charts,

    fullBodyPhotoPairs,
    regionalPhotoPairs,
    regionalNote:
      regionalPhotoPairs.length > 0 ? selections.regionalNote?.trim() || null : null,

    goals,
    assessment: buildAssessment(source, selections.assessmentId),
    posture: buildPosture(source, selections.postureAssessmentIds),

    // Enabled with no number would print "Paketinizde  ders hakkınız
    // kalmıştır." The wizard blocks it; this makes it unrepresentable.
    packageReminder: {
      enabled:
        selections.packageReminder.enabled &&
        selections.packageReminder.remainingLessons !== null,
      remainingLessons: selections.packageReminder.enabled
        ? selections.packageReminder.remainingLessons
        : null,
    },

    coverMessage,
  };
}

/**
 * The instructor's own sentence wins over a curated one — the wizard offers
 * them as one exclusive choice, and honouring both would put two messages on
 * a cover with room for one.
 */
function resolveCoverMessage(
  selections: ReportSelections,
): { key: string; text: string } | null {
  const own = selections.coverMessageCustom?.trim();
  if (own) {
    return {
      key: CUSTOM_COVER_MESSAGE_KEY,
      text: own.slice(0, COVER_MESSAGE_MAX_LENGTH),
    };
  }
  const curated = findCoverMessage(selections.coverMessageKey);
  return curated ? { key: curated.key, text: curated.text } : null;
}

/* -------------------------------------------------------------- defaults */

export interface ReportWarning {
  code: string;
  message: string;
}

/**
 * What the wizard opens with: everything the student actually has, already
 * chosen. The instructor removes rather than assembles, because assembling a
 * report from nothing is the slow path and most reports want most of it.
 */
export function defaultSelections(source: ReportSource): ReportSelections {
  const includedMetrics: MetricKey[] = [];
  for (const key of METRIC_TABLE_ORDER) {
    if (resolveMetric(source, key, undefined)) includedMetrics.push(key);
  }

  // Newest photo per category, paired against the earliest of that category —
  // within the period, since nothing outside it can reach the report anyway.
  const byCategory = new Map<string, PhotoRecord[]>();
  for (const photo of source.photos.filter((p) => inPeriod(source, p.capturedAt))) {
    const bucket =
      photo.category === 'custom' ? `custom:${photo.customLabel}` : photo.category;
    const list = byCategory.get(bucket) ?? [];
    list.push(photo);
    byCategory.set(bucket, list);
  }

  const photoPairs: PhotoPairSelection[] = [];
  for (const list of byCategory.values()) {
    const sorted = [...list].sort((a, b) => (a.capturedAt < b.capturedAt ? -1 : 1));
    const newest = sorted[sorted.length - 1];
    const oldest = sorted[0];
    if (!newest) continue;
    photoPairs.push({
      category: newest.category,
      leftPhotoId: oldest && oldest.id !== newest.id ? oldest.id : null,
      rightPhotoId: newest.id,
    });
  }

  const newestAssessment =
    [...source.assessments]
      .filter((a) => inPeriod(source, a.assessedAt))
      .sort((a, b) => (a.assessedAt < b.assessedAt ? 1 : -1))[0] ?? null;

  /*
   * Unlike the assessment above, not bounded by the period — a studio that
   * runs a posture session once a quarter should still have the wizard offer
   * the latest one by default, even if the report's own dates are narrower.
   * Only the single newest session is pre-selected; comparing against older
   * ones is something the instructor opts into, not a default (YAGNI).
   */
  const newestPosture =
    [...source.postureAssessments].sort((a, b) =>
      a.assessedAt < b.assessedAt ? 1 : -1,
    )[0] ?? null;

  return {
    includedMetrics,
    metricOverrides: {},
    includedCustomLabels: customMetricsOf(source.measurements).map(
      (metric) => metric.labelNormalized,
    ),
    photoPairs,
    regionalNote: null,
    goalIds: source.goals.filter((g) => g.status !== 'cancelled').map((g) => g.id),
    assessmentId: newestAssessment?.id ?? null,
    postureAssessmentIds: newestPosture ? [newestPosture.id] : [],
    packageReminder: { enabled: false, remainingLessons: null },
    coverMessageKey: null,
    coverMessageCustom: null,
  };
}

/**
 * Non-blocking readiness notes for wizard step 9.
 *
 * These warn; they never gate. Missing optional data must not stop a report
 * (PRD §18) — the studio decides whether the report is worth sending.
 */
export function collectWarnings(
  source: ReportSource,
  snapshot: ReportSnapshot,
): ReportWarning[] {
  const warnings: ReportWarning[] = [];

  if (snapshot.metrics.length === 0) {
    warnings.push({
      code: 'NO_MEASUREMENTS',
      message: 'Bu dönem için ölçüm bulunmuyor.',
    });
  }
  if (snapshot.fullBodyPhotoPairs.length + snapshot.regionalPhotoPairs.length === 0) {
    warnings.push({
      code: 'NO_PHOTOS',
      message: 'Rapora hiç gelişim fotoğrafı eklenmedi.',
    });
  }
  if (snapshot.goals.length === 0) {
    warnings.push({ code: 'NO_GOALS', message: 'Rapora hiç hedef eklenmedi.' });
  }
  if (!snapshot.assessment) {
    warnings.push({
      code: 'NO_ASSESSMENT',
      message: 'Rapora eğitmen değerlendirmesi eklenmedi.',
    });
  }
  if (snapshot.posture.length === 0) {
    warnings.push({
      code: 'NO_POSTURE',
      message: 'Rapora postür analizi eklenmedi.',
    });
  }

  const blocked = snapshot.metrics.find(
    (m) => m.comparisonBlockedReason === 'MUSCLE_UNIT_MISMATCH',
  );
  if (blocked) {
    warnings.push({
      code: 'MUSCLE_UNIT_MISMATCH',
      message: 'Kas ölçümlerinin birimleri farklı olduğu için karşılaştırma yapılamıyor.',
    });
  }

  /*
   * Since the period bounds the data, an instructor who picks dates with
   * little in them gets a thin report rather than a wrong one. These say so
   * before the report is built, and neither blocks it (PRD §18).
   */
  const measured = withinPeriod(source, source.measurements);

  if (measured.length === 0 && source.measurements.length > 0) {
    warnings.push({
      code: 'NO_MEASUREMENT_IN_PERIOD',
      message:
        'Seçilen dönemde ölçüm yok. Dönem dışındaki ölçümler rapora girmez — dönemi genişletin ya da “Tüm zamanlar”ı seçin.',
    });
  } else if (measured.length === 1 && source.measurements.length > 1) {
    warnings.push({
      code: 'SINGLE_MEASUREMENT_IN_PERIOD',
      message:
        'Seçilen dönemde tek ölçüm var, bu yüzden değişim gösterilemiyor. Karşılaştırma için dönemi genişletin.',
    });
  }

  const photosInPeriod = source.photos.filter(
    (p) => p.capturedAt >= source.period.start && p.capturedAt <= source.period.end,
  ).length;
  if (photosInPeriod === 0 && source.photos.length > 0) {
    warnings.push({
      code: 'NO_PHOTO_IN_PERIOD',
      message: 'Öğrencinin fotoğrafları var ama hiçbiri seçilen dönemde çekilmemiş.',
    });
  }

  return warnings;
}
